// ── Counting days the way a person counts them ───────────────────────────────
//
// "Plan ends Sep 19" and "Next reset in 30 days" sit next to each other on the
// Credits page, and they disagreed. Two independent causes, both of which have
// to be fixed or the number is still wrong some of the time:
//
// 1. ROUNDING UP. The old arithmetic was
//        Math.ceil((target - now) / 86400000)
//    Any fraction rounds up, so 29.2 days remaining renders as 30. Nobody
//    counts days that way — with 29 days and a few hours left, a person says
//    29.
//
// 2. A DATE COLUMN PARSED AS UTC. `subscriptions.next_billing_date` is a
//    Postgres `date`, so it arrives as "2026-09-19" and `new Date()` reads that
//    as midnight UTC. West of Greenwich that instant is the EVENING OF THE
//    18TH, so the countdown was measured to a different day than the one
//    printed beside it. In America/Denver the two were a full day apart.
//
// The fix for both is to stop measuring elapsed milliseconds and compare
// CALENDAR DAYS in the viewer's own timezone: normalise each end to local
// midnight, then subtract. That is the question actually being asked — "how
// many days until that date on my calendar?" — and it makes the count agree
// with the date rendered next to it.

/**
 * Treat a value as a calendar date in the VIEWER's timezone.
 *
 * A date-only string ("2026-09-19") is deliberately re-read as local midnight
 * rather than UTC midnight: it carries no time and no zone, so interpreting it
 * as UTC silently shifts it a day for most of the world. A full timestamp is
 * left alone and simply truncated to its local day.
 */
const toLocalMidnight = (value) => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (dateOnly) {
      const [, y, m, d] = dateOnly;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
    // A midnight-UTC timestamp is how Postgres serialises a `date` over JSON.
    // It means the same thing as the bare string above, so it gets the same
    // treatment — otherwise the fix works for one shape and not the other.
    const midnightUtc = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(\.000)?Z$/.exec(value.trim());
    if (midnightUtc) {
      const [, y, m, d] = midnightUtc;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

/**
 * Whole calendar days from `now` until `target`, in the viewer's timezone.
 *
 * Negative once the date has passed, 0 on the day itself. Null when the input
 * cannot be read as a date, so callers can distinguish "no date" from "today"
 * — rendering "0 days" for a missing value is how a blank becomes a deadline.
 *
 * Rounds rather than floors the final division purely to absorb DST: a day
 * containing a clock change is 23 or 25 hours long, which would otherwise make
 * a whole number of days come out as 0.96 or 1.04.
 */
export function calendarDaysUntil(target, now = new Date()) {
  const end = toLocalMidnight(target);
  const start = toLocalMidnight(now);
  if (!end || !start) return null;
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

/** Days until, floored at 0 — for countdowns that must not go negative. */
export function daysRemaining(target, now = new Date()) {
  const days = calendarDaysUntil(target, now);
  return days == null ? null : Math.max(0, days);
}
